<template>
  <div>
    <!-- Page Header -->
    <div class="mb-5">
      <div class="headline saPrimary--text">
        {{ $t("screener.title") }}
      </div>
      <span class="body-1 greyDarken3--text">{{
        $t("screener.list.subtitle")
      }}</span>
    </div>

    <!-- Filters -->
    <v-card class="mb-3">
      <v-card-text>
        <v-layout row wrap>
          <v-flex xs12 sm12 md3>
            <!-- Schedule Type -->
            <v-select
              v-model="schedule_type"
              :items="DEFINES.SCREENING_SCHEDULE_TYPES"
              :label="$t('rms.screening_schedule_type')"
              item-text="text"
              item-value="value"
              hide-details
              color="saSecondary"
              @input="getScreeningsFromApi()"
            ></v-select>
          </v-flex>
          <v-divider vertical class="mx-3"></v-divider>
          <v-flex xs12 sm12 md3>
            <!-- Screening Type -->
            <v-select
              v-model="screening_type"
              :items="DEFINES.SCREENING_TYPES"
              :label="'Event Type'"
              item-text="text"
              item-value="value"
              hide-details
              color="saSecondary"
              @input="getScreeningsFromApi()"
            ></v-select>
          </v-flex>
          <v-divider vertical class="mx-3"></v-divider>
          <!-- From / To Date -->
          <v-flex xs12 sm12 md4>
            <v-form ref="duration">
              <v-layout row wrap>
                <!-- From date -->
                <v-flex xs12 sm12 md5 pr-2>
                  <v-menu
                    v-model="fromDateMenu"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                    color="saSecondary"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filters.fromDate"
                        label="From Date (Default last 7 days)"
                        readonly
                        color="saSecondary"
                        v-on="on"
                        clearable
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filters.fromDate"
                      color="saSecondary"
                      :show-current="new Date().toISOString().substr(0, 10)"
                      @input="fromDateMenu = false"
                    ></v-date-picker> </v-menu
                ></v-flex>
                <!-- To date -->
                <v-flex xs12 sm12 md5 pl-2>
                  <v-menu
                    class="d-flex"
                    v-model="toDateMenu"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                    color="saSecondary"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filters.toDate"
                        label="To Date (Default next 7 Days)"
                        readonly
                        color="saSecondary"
                        v-on="on"
                        clearable
                        :rules="
                          filters.fromDate
                            ? [
                                rules.greaterThanDate(
                                  filters.fromDate,
                                  filters.toDate
                                ),
                              ]
                            : []
                        "
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filters.toDate"
                      color="saSecondary"
                      :show-current="new Date().toISOString().substr(0, 10)"
                      @input="toDateMenu = false"
                    ></v-date-picker> </v-menu
                ></v-flex>

                <v-flex xs12 sm12 md2>
                  <v-btn
                    color="saPrimary"
                    dark
                    @click.stop="getScreeningsFromApi"
                    icon
                    ><v-icon>search</v-icon></v-btn
                  ></v-flex
                >
              </v-layout></v-form
            >
          </v-flex>
          <v-spacer></v-spacer>
          <v-btn color="saPrimary" class="mx-0" dark @click.stop="resetFilters"
            >Reset Filters</v-btn
          >
        </v-layout>
      </v-card-text>
    </v-card>

    <!-- Page Content -->
    <v-card>
      <v-card-title>
        <div v-if="total" class="title">
          <span>Total : </span>
          <b>{{ total }}</b>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          class="custom-placeholer-color"
          append-outer-icon="search"
          v-model="search"
          label="Search"
          :placeholder="'Search by screening title, id, company title or id'"
          clear-inner-icon="rms_close"
          clearable
          single-line
          hide-details
          @keyup.enter="getScreeningsFromApi"
          @click:clear="clearSearch"
          @click:append-outer="applySearch"
          color="saSecondary"
        ></v-text-field>
      </v-card-title>
      <v-data-table
        :headers="table_headers"
        :loading="loading"
        :items="list"
        :pagination.sync="pagination"
        :total-items="total"
        :rowsPerPageItems="rowsPerPageItems"
        item-key="id"
      >
        <template v-slot:items="props">
          <tr :style="{ cursor: 'pointer' }">
            <td class="subheading">{{ props.item.id }}</td>
            <td>
              <v-img
                width="50"
                height="50"
                v-if="props.item.logo_url"
                :src="props.item.logo_url"
              ></v-img>
            </td>
            <td>
              <v-tooltip top color="greyDarken1">
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ props.item.title | truncate }}</span>
                </template>
                <span class="white--text body-1">{{ props.item.title }}</span>
              </v-tooltip>
            </td>
            <td>
              <span
                v-if="
                  props.item.screening_type == DEFINES.SCREENING_PLAN_TYPE.FREE
                "
                class="success--text"
              >
                <b>{{
                  DEFINES.SCREENING_PLAN_TYPE_STR[props.item.screening_type]
                }}</b></span
              >
              <span
                v-if="
                  props.item.screening_type ==
                  DEFINES.SCREENING_PLAN_TYPE.PREMIUM
                "
                class="blue--text"
              >
                <b>{{
                  DEFINES.SCREENING_PLAN_TYPE_STR[props.item.screening_type]
                }}</b></span
              >
              <span
                v-if="
                  props.item.screening_type ==
                  DEFINES.SCREENING_PLAN_TYPE.ENTERPRISE
                "
                class="orange--text"
              >
                <b>{{
                  DEFINES.SCREENING_PLAN_TYPE_STR[props.item.screening_type]
                }}</b></span
              >
            </td>
            <td>
              <v-tooltip top color="greyDarken1">
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{
                    props.item.company.title | truncate
                  }}</span>
                </template>
                <span class="white--text body-1">{{
                  props.item.company.title
                }}</span>
              </v-tooltip>
            </td>

            <td>
              {{ fmtTime(props.item.screening_starts_at, props.item.company) }}
              (to)
              {{ fmtTime(props.item.screening_ends_at, props.item.company) }}
            </td>
            <td>
              {{ props.item.total_registrants }}&nbsp;/&nbsp;{{
                props.item.limitation
              }}
            </td>
            <td>{{ props.item.num_of_enrollments }}</td>
            <td class="text-xs-left">
              <v-chip
                v-if="props.item.published"
                class="success success--text"
                small
                outline
                >
                <v-tooltip v-if="props.item.is_unlisted" top color="greyDarken1">
                  <template v-slot:activator="{on}">
                    <v-icon v-on="on" v-if="props.item.is_unlisted" small class="pr-1 red--text">mdi-lock</v-icon>
                  </template>
                  <span class="white--text body-1">{{$t('screener.secret_event.tooltip')}}</span>
                </v-tooltip>
                <b>{{
                  DEFINES.SCREENER_STATUS[props.item.published]
                }}</b></v-chip
              >
              <v-chip v-else class="grey grey--text" small outline
                ><b>{{
                  DEFINES.SCREENER_STATUS[props.item.published]
                }}</b></v-chip
              >
              <v-chip
                v-if="isScreeningPaid(props.item)"
                class="saSecondary saSecondary--text"
                small
                outline
                ><b>{{ $t("screener.status.paid") }}</b></v-chip
              >
            </td>
            <td>{{ fmtTime(props.item.created_at, props.item.company) }}</td>
            <td class="text-xs-center">
              <!-- <v-tooltip slot="append" top color="white">
                <v-btn slot="activator" icon small dark @click.stop="">
                  <v-icon color="secondary"
                    >$vuetify.icons.rms_visibility</v-icon
                  >
                </v-btn>
                <span class="greyDarken3--text body-1">{{
                  $t("rms.tooltip.view")
                }}</span>
              </v-tooltip> -->

              <!-- <v-tooltip slot="append" top color="white">
                <v-btn slot="activator" icon small dark @click.stop="">
                  <v-icon color="secondary">$vuetify.icons.rms_delete</v-icon>
                </v-btn>
                <span class="greyDarken3--text body-1">{{
                  $t("rms.tooltip.delete")
                }}</span>
              </v-tooltip> -->
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import i18n from "@/i18n";
import { utcToTz } from "@/helper";
import rules from "@/validation";
import moment from 'moment';
import momentTz from 'moment-timezone';
export default {
  name: "AdminScreeningList",
  async beforeRouteEnter(to, from, next) {
    await next((vm) => {
      vm.getScreeningsFromApi();
    });
  },
  data() {
    return {
      rules,
      table_headers: [
        {
          text: i18n.t("screener.table.header.screener_id"),
          align: "left",
          sortable: true,
          value: "id",
        },
        {
          text: i18n.t("screener.table.header.logo"),
          align: "left",
          sortable: false,
          value: "logo_url",
        },
        {
          text: i18n.t("screener.table.header.title"),
          align: "left",
          sortable: true,
          value: "title",
        },
        {
          text: "Type",
          align: "left",
          sortable: true,
          value: "screening_type",
        },
        {
          text: i18n.t("company.title"),
          align: "left",
          sortable: false,
          value: "company.title",
        },
        {
          text: i18n.t("screener.table.header.start_end_date"),
          align: "left",
          sortable: false,
          value: "start_ends_date",
        },
        {
          text: i18n.t("screener.table.header.virtual_seats"),
          align: "left",
          sortable: true,
          value: "limitation",
        },
        {
          text: "Enrollments",
          align: "left",
          sortable: true,
          value: "num_of_enrollments",
        },
        {
          text: i18n.t("screener.table.header.status"),
          align: "left",
          sortable: false,
          value: "published",
        },
        {
          text: i18n.t("screener.table.header.created_at"),
          align: "left",
          sortable: true,
          value: "created_at",
        },
        {
          text: i18n.t("screener.table.header.action"),
          align: "left",
          sortable: false,
          value: "action",
        },
      ],
      search: "",
      schedule_type: 40,
      screening_type: null,
      total: 0,
      loading: false,
      rowsPerPageItems: [5, 10, 25, 50, 100],
      pagination: {
        sortBy: "created_at",
        descending: true,
        page: 1,
        rowsPerPage: 10,
      },
      filters: this.initializeFilters(),
      fromDateMenu: false,
      toDateMenu: false,
    };
  },
  computed: {
    ...mapState({
      list: (state) => state.adminScreener.list.data,
    }),
    ...mapGetters("adminScreener", {
      isScreeningPaid: "isScreeningPaid",
    }),
  },
  watch: {
    pagination: {
      handler: "getScreeningsFromApi",
      deep: true,
    },
  },
  methods: {
    initializeFilters() {
      return {
        fromDate: this.moment().subtract(7, "day").toISOString().substr(0, 10),
        toDate: this.moment().add(7, "day").toISOString().substr(0, 10),
      };
    },

    applySearch() {
      if (this.search.length) this.getScreeningsFromApi();
    },
    clearSearch() {
      this.search = null;
      this.getScreeningsFromApi();
    },
    async getScreeningsFromApi() {
      if (!this.$refs.duration.validate()) {
        return false;
      }

      this.loading = true;
      const { sortBy, descending, page, rowsPerPage } = this.pagination;
      var offset = this.getPaginationOffset(rowsPerPage, page);

      // Payload
      let payload = {
        offset: offset,
        limit: rowsPerPage,
        sort: sortBy,
        sorting_order: descending,
        schedule_type: this.schedule_type,
        screening_type: this.screening_type,
        search: this.search,
        filters: this.filters,
      };

      try {
        var response = await this.$store.dispatch(
          "adminScreener/list",
          payload
        );
        this.total = response.screeners.total;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$store.dispatch("app/showMessage", {
          message: error.message,
          color: this.DEFINES.SNACKBAR_ERROR_COLOR,
        });
      }
    },
    getPaginationOffset: (recordPerPage, page) => recordPerPage * (page - 1),
    // convert utc to comapny timezone
    fmtTime(input, company) {
      if (input && company) {
        // OS local timezone
        var timezone = momentTz.tz.guess(true);
        return utcToTz(input, timezone, "MMMM Do YYYY, h:mm a");
      }
    },
    resetFilters() {
      this.search = null;
      this.schedule_type = 40;
      this.screening_type = null;
      this.fromDateMenu = false;
      this.toDateMenu = false;
      this.filters = this.initializeFilters();
      this.getScreeningsFromApi();
    },
  },
};
</script>
